import React, { useState, useEffect } from 'react'
import { TextField, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useShopChannelRules } from '../../hooks/use-shop-channel-type'

interface QuantityInputProps {
  quantity: number
  minQuantity?: number
  maxQuantity?: number
  onQuantityChange?: (value: number) => void
  allowInput?: boolean
  disabled?: boolean
  inputClassName?: string
}

const useStyles = makeStyles()(theme => ({
  quantityInputWrapper: {
    width: 40,
  },
  quantityInput: {
    width: '100%',
    '& input': {
      textAlign: 'center',
      padding: theme.spacing(1),
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '-moz-appearance': 'textfield',
    },
  },
}))

/*
 * This component is a quantity input field for products to change quantity.
 */
const QuantityInput: React.FC<QuantityInputProps> = ({
  quantity,
  minQuantity = 0,
  maxQuantity,
  onQuantityChange,
  allowInput,
  disabled = false,
  inputClassName,
}) => {
  const { classes } = useStyles()
  const [inputValue, setInputValue] = useState(quantity.toString())
  const { allowInputFieldOnProductQuantity } = useShopChannelRules()

  useEffect(() => {
    setInputValue(quantity.toString())
  }, [quantity])

  const handleChange = (value: string): void => {
    const digitsOnly = value.replaceAll(/\D+/g, '')

    if (digitsOnly === '') {
      setInputValue('')
      return
    }

    let numericValue = Number(digitsOnly)

    if (numericValue < minQuantity) {
      numericValue = minQuantity
    }

    if (maxQuantity !== undefined && numericValue > maxQuantity) {
      numericValue = maxQuantity
    }

    setInputValue(numericValue.toString())

    if (numericValue !== quantity) {
      onQuantityChange?.(numericValue)
    }
  }

  const handleBlur = (): void => {
    if (inputValue === '') {
      setInputValue(quantity.toString())
      return
    }

    let value = Number(inputValue)

    if (!Number.isInteger(value) || value < minQuantity) {
      value = quantity
    }

    if (maxQuantity && value > maxQuantity) {
      value = maxQuantity
    }

    setInputValue(value.toString())

    if (value !== quantity) {
      onQuantityChange?.(value)
    }
  }

  return (
    <Box className={classes.quantityInputWrapper}>
      <TextField
        value={inputValue}
        type="text"
        size="small"
        disabled={allowInput === false || !allowInputFieldOnProductQuantity || disabled}
        className={classes.quantityInput}
        inputProps={{
          min: minQuantity,
          max: maxQuantity,
          step: 1,
          inputMode: 'numeric',
          pattern: '[0-9]*',
        }}
        InputProps={{
          classes: {
            root: inputClassName,
            notchedOutline: inputClassName,
            input: inputClassName,
          },
        }}
        onChange={e => handleChange(e.target.value)}
        onBlur={handleBlur}
      />
    </Box>
  )
}

export default QuantityInput
